import React, { useEffect, useRef, useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";

const shellStyle = {
  background: "linear-gradient(135deg, rgba(28,36,53,0.96) 0%, rgba(20,27,40,0.96) 100%)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 12,
  padding: "8px 10px",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.03), 0 8px 18px rgba(0,0,0,0.22)",
};

const baseInputStyle = {
  background: "rgba(11,17,26,0.9)",
  color: "#fff",
  border: "1px solid rgba(127,167,255,0.28)",
  borderRadius: 10,
  fontWeight: 600,
  letterSpacing: "0.1px",
  boxShadow: "0 0 0 2px rgba(31,142,241,0.08)",
};

const navButtonStyle = {
  minWidth: 72,
  borderRadius: 10,
  fontWeight: 700,
  border: "1px solid rgba(255,255,255,0.14)",
  background: "linear-gradient(135deg, rgba(52,62,85,0.95) 0%, rgba(37,44,60,0.95) 100%)",
  color: "#e8eefb",
};

const labelStyle = {
  color: "rgba(255,255,255,0.86)",
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 6,
};

function TinyWeekSelector({
  label = "週期",
  options = [],
  value = "",
  onChange,
  placeholder = "",
  includeEmptyOption = false,
  emptyOptionLabel = "選擇週期",
  markFirstAsNextWeek = false,
  renderOptionLabel,
  getOptionValue,
  getOptionKey,
  inputStyle = {},
  bsSize,
  showNav = true,
  prevLabel = "上周",
  nextLabel = "下周",
  navInline = true,
  showMeta = true,
  className = "",
  autoStackOnNarrow = true,
  stackBreakpoint = 460,
}) {
  const rootRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (!rootRef.current || !autoStackOnNarrow) return undefined;
    const el = rootRef.current;
    const check = () => {
      setIsNarrow(el.clientWidth > 0 && el.clientWidth < stackBreakpoint);
    };
    check();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => check());
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoStackOnNarrow, stackBreakpoint]);

  const idx = options.findIndex((opt) => {
    const optionValue = getOptionValue ? getOptionValue(opt) : opt;
    return optionValue === value;
  });
  const canPrev = idx >= 0 && idx < options.length - 1;
  const canNext = idx > 0;

  const resolveLabel = (opt, optionIndex) => {
    if (renderOptionLabel) return renderOptionLabel(opt, optionIndex);
    const text = getOptionValue ? getOptionValue(opt) : opt;
    if (markFirstAsNextWeek && optionIndex === 0 && text) return `${text} (下一週)`;
    return text;
  };

  const resolveValue = (opt) => (getOptionValue ? getOptionValue(opt) : opt);
  const resolveKey = (opt, optionIndex) => (getOptionKey ? getOptionKey(opt, optionIndex) : `${resolveValue(opt)}_${optionIndex}`);
  const showPlaceholderOnly = placeholder && !includeEmptyOption;
  const hasValue = !!value;
  const useInlineNav = navInline && !(autoStackOnNarrow && isNarrow);

  return (
    <FormGroup className={className} innerRef={rootRef}>
      {label ? <Label style={labelStyle}>{label}</Label> : null}
      <div style={shellStyle}>
        <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: useInlineNav ? "nowrap" : "wrap" }}>
          {showNav && useInlineNav ? (
            <Button
              size="sm"
              disabled={!canPrev}
              style={{ ...navButtonStyle, opacity: canPrev ? 1 : 0.45 }}
              onClick={() => onChange && onChange(resolveValue(options[idx + 1]))}
            >
              <i className="fa fa-chevron-left mr-1" />
              {prevLabel}
            </Button>
          ) : null}
          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
            <i
              className="fa fa-calendar"
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                // 跟著導覽的 active 色走（adminTheme.navActive）。
                // 原本硬寫 rgba(122,168,255,0.9) —— 那是全站第四種藍，
                // 而它就顯示在標題 icon 旁邊，兩種藍並排看起來像沒對齊。
                // 這支被 14 個頁面共用，所以改這一行等於 14 頁一起統一。
                color: "var(--tph-nav-active, #667eea)",
                fontSize: 12,
                pointerEvents: "none",
              }}
            />
            <Input
              type="select"
              value={value}
              onChange={(e) => onChange && onChange(e.target.value)}
              style={{ ...baseInputStyle, paddingLeft: 30, ...inputStyle }}
              bsSize={bsSize}
            >
              {includeEmptyOption ? <option value="">{emptyOptionLabel}</option> : null}
              {showPlaceholderOnly && !hasValue ? <option value="">{placeholder}</option> : null}
              {options.map((opt, optionIndex) => (
                <option key={resolveKey(opt, optionIndex)} value={resolveValue(opt)}>
                  {resolveLabel(opt, optionIndex)}
                </option>
              ))}
            </Input>
          </div>
          {showNav && useInlineNav ? (
            <Button
              size="sm"
              disabled={!canNext}
              style={{ ...navButtonStyle, opacity: canNext ? 1 : 0.45 }}
              onClick={() => onChange && onChange(resolveValue(options[idx - 1]))}
            >
              {nextLabel}
              <i className="fa fa-chevron-right ml-1" />
            </Button>
          ) : null}
        </div>
        {showNav && !useInlineNav ? (
          <div className="d-flex mt-2" style={{ gap: 8 }}>
            <Button
              size="sm"
              disabled={!canPrev}
              style={{ ...navButtonStyle, opacity: canPrev ? 1 : 0.45 }}
              onClick={() => onChange && onChange(resolveValue(options[idx + 1]))}
            >
              <i className="fa fa-chevron-left mr-1" />
              {prevLabel}
            </Button>
            <Button
              size="sm"
              disabled={!canNext}
              style={{ ...navButtonStyle, opacity: canNext ? 1 : 0.45 }}
              onClick={() => onChange && onChange(resolveValue(options[idx - 1]))}
            >
              {nextLabel}
              <i className="fa fa-chevron-right ml-1" />
            </Button>
          </div>
        ) : null}
        {showMeta ? (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginTop: 7, fontSize: 11, color: "rgba(255,255,255,0.58)" }}
          >
            <span>共 {options.length} 周期</span>
            <span>{idx >= 0 ? `第 ${idx + 1} / ${options.length}` : "尚未選擇"}</span>
          </div>
        ) : null}
      </div>
    </FormGroup>
  );
}

export default TinyWeekSelector;
